import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  IntakePageWrapper,
  IntakeNavTabs,
  TabBadge,
  ViewContainer,
} from "./MailIntakeStyled";
import AssignView from "./components/AssignView";
import ListView from "./components/ListView";
import UploadView from "./components/UploadView";
import StatsView from "./components/StatsView";
import DeadlineCardsSection from "./components/DeadlineCardsSection";
import useAuth from "../../hooks/useAuth";
import {
  refreshIntakeCounts,
  setActiveTab,
} from "../../slice/MailIntakeSlice";
import {
  fetchDeadlineCards,
  selectDeadlineCardsTotal,
} from "../../slice/InboxSlice";

const MailIntake = () => {
  // activeTab lives on the slice so ListView's row-click handler can
  // jump straight to the Assign tab without prop-drilling a setter.
  const { isAdmin } = useAuth();
  const dispatch = useDispatch();
  const { counts, activeTab } = useSelector((state) => state.mailIntake);
  const deadlineTotal = useSelector(selectDeadlineCardsTotal);
  const handleTabChange = (key) => dispatch(setActiveTab(key));


  // Seed the tab-badge counts as soon as the page mounts and re-seed when
  // the user switches tabs. Without this, `state.counts` only gets populated
  // from the mock path of `fetchQueueList`; in production the real queue
  // endpoint doesn't echo counts, so both badges rendered empty forever.
  // Individual actions (claim/assign/skip/delete) also dispatch this thunk
  // via AssignView so the numbers stay live after mutations.
  useEffect(() => {
    dispatch(refreshIntakeCounts());
    // Proposed-deadline queue rides the same refresh cadence so the
    // Deadlines tab badge is live on page load and after tab hops.
    dispatch(fetchDeadlineCards());
  }, [dispatch, activeTab]);

  const tabItems = useMemo(() => {
    const items = [
      {
        key: "assign",
        label: (
          <span>
            Assign
            {counts?.pending > 0 && (
              <TabBadge active={activeTab === "assign"}>{counts.pending}</TabBadge>
            )}
          </span>
        ),
      },
      {
        key: "list",
        label: (
          <span>
            List
            {/*
              The List tab is the sorter's active queue: work they can still
              act on. That's counts.active (uploading + pending + claimed +
              skipped), which matches the default scope of /mail-intake/queue/.
              Assigned rows have moved on to their matter; deleted rows are
              reachable via the status dropdown in the list but don't belong
              in the badge count.
            */}
            {counts?.active > 0 && (
              <TabBadge active={activeTab === "list"}>{counts.active}</TabBadge>
            )}
          </span>
        ),
      },
      {
        key: "deadlines",
        label: (
          <span>
            Deadlines
            {/* The badge counts the whole queue, not the capped page the
                list endpoint returned — otherwise a 709-deadline backlog
                wears a "100" badge. */}
            {deadlineTotal > 0 && (
              <TabBadge active={activeTab === "deadlines"}>
                {deadlineTotal}
              </TabBadge>
            )}
          </span>
        ),
      },
      {
        key: "upload",
        label: "Upload",
      },
    ];

    if (isAdmin) {
      items.push({
        key: "stats",
        label: "Stats Admin",
      });
    }

    return items;
  }, [activeTab, counts, deadlineTotal, isAdmin]);

  const renderView = () => {
    switch (activeTab) {
      case "assign":
        return <AssignView />;
      case "list":
        return <ListView />;
      case "deadlines":
        return <DeadlineCardsSection />;
      case "upload":
        return <UploadView />;
      case "stats":
        return <StatsView />;
      default:
        return <AssignView />;
    }
  };

  return (
    <IntakePageWrapper>
      <IntakeNavTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />
      <ViewContainer>{renderView()}</ViewContainer>
    </IntakePageWrapper>
  );
};

export default MailIntake;
